'use client';

import clsx from 'clsx';
import React from 'react';

const DEFAULT_ICON = (
    <svg
        className={`inline-block h-[16px] w-[16px] align-middle`}
        viewBox="0 0 1024 1024"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M235.093333 215.466667v85.333333h427.946667l-477.866667 477.866667 60.16 60.16 477.866667-477.866667v427.946667h85.333333V215.466667z"></path>
    </svg>
);

const DEFAULT_ROUNDED_MOBILE = 32;
const DEFAULT_ROUNDED = 64;
const DEFAULT_ICON_SIZE = 20;
const DEFAULT_ICON_SIZE_MOBILE = 16;

export function FooterButton({
    text,
    textColor,
    textHoverColor,
    borderColor,
    boderHoverColor,
    bgColor,
    bgHoverColor,
    icon = DEFAULT_ICON,
    withIcon = true,
    iconColor,
    iconHoverColor,
    iconSize4Mobile,
    iconSize,
    className,
    keepCenter = false,
    onClick,
    disabled = false,
}: Readonly<{
    keepCenter?: boolean;
    text: string;
    textColor: string;
    textHoverColor: string;
    borderColor: string;
    boderHoverColor: string;
    bgColor?: string;
    bgHoverColor: string;
    className?: string;
    withIcon?: boolean;
    icon?: React.ReactNode;
    iconColor?: string;
    iconHoverColor?: string;
    iconSize4Mobile?: number[];
    iconSize?: number[];
    disabled?: boolean;

    onClick: () => void;
}>) {
    const [isHovered, setIsHovered] = React.useState(false);

    const finalIcon = icon === undefined ? DEFAULT_ICON : icon;
    const finalIconSize = iconSize ?? [DEFAULT_ICON_SIZE, DEFAULT_ICON_SIZE];
    const finalIconSize4Mobile = iconSize4Mobile ?? [
        DEFAULT_ICON_SIZE_MOBILE,
        DEFAULT_ICON_SIZE_MOBILE,
    ];

    const finalKeepCenter = keepCenter ?? false;

    return (
        <button
            disabled={disabled}
            style={{
                borderColor: isHovered ? boderHoverColor : borderColor,
                backgroundColor:
                    bgColor === undefined ? 'transparent' : bgColor,
            }}
            className={clsx(
                `relative z-20 overflow-hidden rounded-[32px] border sm:rounded-[64px]`,
                {
                    'cursor-not-allowed opacity-60': disabled,
                },
                className,
            )}
            onMouseEnter={() => {
                if (disabled) return;
                setIsHovered(true);
            }}
            onMouseLeave={() => {
                if (disabled) return;
                setIsHovered(false);
            }}
            onClick={() => {
                if (disabled) return;
                onClick();
            }}
        >
            <div
                id="hovered-bg"
                style={{
                    width: isHovered ? '102%' : '0%',
                    transition: 'width 0.5s ease-in-out',
                    position: 'absolute',
                    left: -1,
                    top: 0,
                    bottom: 0,
                    backgroundColor: bgHoverColor,
                }}
                className="-z-10 hidden rounded-[32px] sm:block sm:rounded-[64px]"
            />
            <div
                id="content"
                style={{
                    borderColor: borderColor,
                    color: isHovered ? textHoverColor : textColor,
                    transition: 'color 0.5s ease-in-out,fill 0.5s ease-in-out',
                }}
                className={clsx(
                    'z-[999] hidden h-full flex-row items-center justify-center gap-[8px] px-[20px] sm:flex',
                    {
                        'justify-center': keepCenter,
                        'justify-between': !keepCenter,
                    },
                )}
            >
                <div className="whitespace-nowrap">{text}</div>
                {withIcon && (
                    <div
                        style={{
                            fill: isHovered ? iconHoverColor : iconColor,
                            transition:
                                'color 0.5s ease-in-out,fill 0.5s ease-in-out',
                            width: `${finalIconSize[0]}px`,
                            height: `${finalIconSize[1]}px`,
                        }}
                        className={clsx(`flex items-center justify-center`)}
                    >
                        {finalIcon}
                    </div>
                )}
            </div>
            <div
                id="content4mobile"
                style={{
                    borderColor: borderColor,
                    color: textColor,
                    transition: 'color 0.5s ease-in-out,fill 0.5s ease-in-out',
                }}
                className={clsx(
                    'flex flex-row items-center px-[20px] sm:hidden',
                    {
                        'justify-center gap-[8px]': keepCenter,
                        'justify-between': !keepCenter,
                    },
                )}
            >
                <div className="whitespace-nowrap">{text}</div>
                {withIcon && (
                    <div
                        style={{
                            fill: iconColor,
                            width: `${finalIconSize4Mobile[0]}px`,
                            height: `${finalIconSize4Mobile[1]}px`,
                        }}
                        className={clsx(`flex items-center justify-center`)}
                    >
                        {finalIcon}
                    </div>
                )}
            </div>
        </button>
    );
}
